import { StyleSheet, css } from 'aphrodite'
import type React from 'react'

type FooterVariant = 'standard' | 'tag'

interface ProjectModalFooterProps {
  onCancel: () => void
  onNext: () => void
  onBack: () => void
  isFirstStep: boolean
  nextLabel: string
  isLoading?: boolean
  showDeclineButton?: boolean
  showSubmit?: boolean
  isViewFlow?: boolean
  isNextDisabled?: boolean
  onSaveDraft?: () => void
  showSaveDraft?: boolean
  isSavingDraft?: boolean
  validationError?: string | null
  submitError?: string | null
  onDismissSubmitError?: () => void
  onTrySubmitWithErrors?: () => void
  /** Close button handler - closes the form directly */
  onClose?: () => void
  /** Whether to show the Close button */
  showCloseButton?: boolean
  /** Button styling variant - 'standard' for Project Terms/Dispatches, 'tag' for Tags */
  variant?: FooterVariant
}

const ProjectModalFooter: React.FC<ProjectModalFooterProps> = ({
  onCancel,
  onNext,
  onBack,
  isFirstStep,
  nextLabel = 'Submit',
  isLoading = false,
  showDeclineButton = false,
  isViewFlow = false,
  isNextDisabled = false,
  onSaveDraft,
  showSaveDraft = false,
  isSavingDraft = false,
  validationError,
  submitError,
  onDismissSubmitError,
  onTrySubmitWithErrors,
  onClose,
  showCloseButton = false,
  variant = 'standard',
}) => {
  // Tag variant uses neutral styling for all buttons
  const isTagVariant = variant === 'tag'
  // Handle click on disabled button to show validation errors
  const handleNextClick = () => {
    if (isNextDisabled && onTrySubmitWithErrors) {
      onTrySubmitWithErrors()
    } else {
      onNext()
    }
  }
  return (
    <div className={css(styles.footerWrapper)}>
      {submitError && (
        <div className={css(styles.submitError)}>
          <div className={css(styles.submitErrorContent)}>
            <i className="fa-solid fa-circle-exclamation" />
            <span>{submitError}</span>
          </div>
          {onDismissSubmitError && (
            <button
              type="button"
              onClick={onDismissSubmitError}
              className={css(styles.submitErrorDismiss)}
            >
              <i className="fa-solid fa-times" />
            </button>
          )}
        </div>
      )}
      {validationError && (
        <div className={css(styles.validationError)}>{validationError}</div>
      )}
      <div className={css(styles.footer)}>
        <div className={css(styles.leftButtons)}>
          {showSaveDraft && onSaveDraft && (
            <button
              onClick={onSaveDraft}
              className={css(styles.saveDraftBtn)}
              disabled={isSavingDraft}
            >
              {isSavingDraft ? (
                <div className={css(styles.loadingWrapper)}>
                  <i className="fa-solid fa-spinner fa-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Draft'
              )}
            </button>
          )}
          {showDeclineButton ? (
            <button onClick={onCancel} className={css(styles.declineBtn)}>
              Decline
            </button>
          ) : isViewFlow ? (
            <button onClick={onCancel} className={css(styles.standardBtn)}>
              Close
            </button>
          ) : (
            <>
              <button
                onClick={onCancel}
                className={css(
                  isTagVariant ? styles.tagDeleteBtn : styles.deleteBtn,
                )}
              >
                Delete
              </button>
              {!isFirstStep && (
                <button onClick={onBack} className={css(styles.standardBtn)}>
                  Back
                </button>
              )}
              {showCloseButton && onClose && (
                <button onClick={onClose} className={css(styles.standardBtn)}>
                  Close
                </button>
              )}
            </>
          )}
        </div>
        <div className={css(styles.rightButtons)}>
          <button
            onClick={handleNextClick}
            className={css(
              nextLabel === 'Send'
                ? isTagVariant
                  ? styles.tagSendBtn
                  : styles.sendBtn
                : styles.nextBtn,
              isNextDisabled && styles.nextBtnDisabled,
            )}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className={css(styles.loadingWrapper)}>
                <i className="fa-solid fa-spinner fa-spin" />
                <span>Submitting...</span>
              </div>
            ) : (
              nextLabel
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Standardized button dimensions for consistency
const BUTTON_HEIGHT = '40px'
const BUTTON_MIN_WIDTH = '100px'
const BUTTON_PADDING = '8px 20px'
const BUTTON_FONT_SIZE = '14px'
const BUTTON_FONT_WEIGHT = 600 // semi-bold
const BUTTON_BORDER_RADIUS = '6px'

const styles = StyleSheet.create({
  footerWrapper: {
    borderTop: '1px solid #E5E7EB',
    backgroundColor: 'white',
    marginTop: 'auto',
  },
  submitError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 24px',
    backgroundColor: '#FEF2F2',
    borderBottom: '1px solid #FECACA',
  },
  submitErrorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#DC2626',
    fontSize: '14px',
    fontWeight: 500,
  },
  submitErrorDismiss: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      color: '#991B1B',
    },
  },
  validationError: {
    color: '#DC2626',
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'center',
    padding: '12px 24px',
    backgroundColor: '#FEF2F2',
  },
  footer: {
    padding: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'white',
  },
  leftButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  rightButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  // Delete button - light red background, semi-bold black text
  deleteBtn: {
    backgroundColor: '#FEE2E2', // light red background
    border: '1px solid #FECACA',
    color: '#000000', // black text
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    borderRadius: BUTTON_BORDER_RADIUS,
    ':hover': {
      backgroundColor: '#FECACA',
    },
  },
  // Standard button - white background, semi-bold black text (Back, Close, Draft Save, Next)
  standardBtn: {
    backgroundColor: '#FFFFFF', // white background
    border: '1px solid #D1D5DB',
    color: '#000000', // black text
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    borderRadius: BUTTON_BORDER_RADIUS,
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
    ':disabled': {
      backgroundColor: '#F3F4F6',
      color: '#9CA3AF',
      borderColor: '#E5E7EB',
      cursor: 'not-allowed',
      fontWeight: 400,
    },
  },
  // Legacy cancel button - kept for backwards compatibility, maps to standardBtn
  cancelBtn: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    color: '#000000',
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    borderRadius: BUTTON_BORDER_RADIUS,
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
  },
  declineBtn: {
    backgroundColor: '#FEE2E2',
    border: '1px solid #FECACA',
    color: '#000000',
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    borderRadius: BUTTON_BORDER_RADIUS,
    ':hover': {
      backgroundColor: '#FECACA',
    },
  },
  saveDraftBtn: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    color: '#000000',
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    borderRadius: BUTTON_BORDER_RADIUS,
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
    ':disabled': {
      backgroundColor: '#F3F4F6',
      color: '#9CA3AF',
      borderColor: '#E5E7EB',
      cursor: 'not-allowed',
      fontWeight: 400,
    },
  },
  // Send button - friendly green background, semi-bold black text
  sendBtn: {
    backgroundColor: '#10B981', // friendly green
    color: '#000000', // black text
    border: 'none',
    borderRadius: BUTTON_BORDER_RADIUS,
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    ':hover': {
      backgroundColor: '#059669',
    },
    ':disabled': {
      backgroundColor: '#E5E7EB',
      color: '#6B7280',
      fontWeight: 400,
      cursor: 'not-allowed',
    },
  },
  // Next button - white background, semi-bold black text (standard navigation)
  nextBtn: {
    backgroundColor: '#FFFFFF',
    color: '#000000',
    border: '1px solid #D1D5DB',
    borderRadius: BUTTON_BORDER_RADIUS,
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
    ':disabled': {
      backgroundColor: '#F3F4F6',
      color: '#9CA3AF',
      borderColor: '#E5E7EB',
      fontWeight: 400,
      cursor: 'not-allowed',
    },
  },
  // Next button disabled state (for validation) - white bg, light gray text
  nextBtnDisabled: {
    backgroundColor: '#FFFFFF',
    color: '#6B7280',
    fontWeight: 400,
    border: '1px solid #D1D5DB',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  // Tag variant styles - neutral/default styling (excluded from color coding per requirements)
  // Tag Delete button - neutral gray styling instead of red
  tagDeleteBtn: {
    backgroundColor: '#F3F4F6', // light gray background
    border: '1px solid #D1D5DB',
    color: '#374151', // dark gray text
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    borderRadius: BUTTON_BORDER_RADIUS,
    ':hover': {
      backgroundColor: '#E5E7EB',
      borderColor: '#9CA3AF',
    },
  },
  // Tag Send button - neutral blue styling instead of green
  tagSendBtn: {
    backgroundColor: '#3B82F6', // blue background
    color: '#FFFFFF', // white text
    border: 'none',
    borderRadius: BUTTON_BORDER_RADIUS,
    padding: BUTTON_PADDING,
    height: BUTTON_HEIGHT,
    minWidth: BUTTON_MIN_WIDTH,
    cursor: 'pointer',
    fontSize: BUTTON_FONT_SIZE,
    fontWeight: BUTTON_FONT_WEIGHT,
    ':hover': {
      backgroundColor: '#2563EB',
    },
    ':disabled': {
      backgroundColor: '#E5E7EB',
      color: '#6B7280',
      fontWeight: 400,
      cursor: 'not-allowed',
    },
  },
})

export default ProjectModalFooter
